import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, Plus, Sparkles, Trash2, X as XIcon, ExternalLink, Globe2,
  Rocket, PencilLine, Save, Send, KeyRound, Coins, Upload, ZoomIn, ImagePlus, CalendarClock, CalendarX2,
} from "lucide-react";
import { api } from "@/lib/api";
import ImageSwapLightbox from "@/components/ImageSwapLightbox";
import ImageCropModal from "@/components/ImageCropModal";
import { toast } from "sonner";

const STATUS_META = {
  queued:     { dot: "bg-ink-muted",   label: "Queued" },
  generating: { dot: "bg-brand-amber animate-pulse", label: "Generating" },
  draft:      { dot: "bg-sky-400",     label: "Draft" },
  scheduled:  { dot: "bg-violet-400",  label: "Scheduled" },
  published:  { dot: "bg-emerald-400", label: "Published" },
  failed:     { dot: "bg-rose-400",    label: "Failed" },
};
const selCls = "rounded-md border border-line bg-bg px-2.5 py-2 text-xs text-ink-primary focus:outline-none focus:border-brand-amber/60 w-full";

export default function BulkBatchTab() {
  const [meta, setMeta] = useState(null);
  const [topics, setTopics] = useState([""]);
  const [theme, setTheme] = useState("");
  const [themeCount, setThemeCount] = useState(10);
  const [expanding, setExpanding] = useState(false);
  const [s, setS] = useState({
    word_count: "800-1000", tone: "professional", seo: true, web_search: false,
    schedule: "draft", publish_target: "wordpress", provider_mode: "platform", byok_provider: "",
    share_platforms: [], image_strategy: "branded_card",
  });
  const [creating, setCreating] = useState(false);
  const [batches, setBatches] = useState([]);
  const [review, setReview] = useState(null);
  const pollRef = useRef(null);

  const loadMeta = useCallback(() => {
    api.get("/blog-bulk/meta").then((r) => {
      setMeta(r.data);
      if (!r.data.wordpress_connected) setS((v) => ({ ...v, publish_target: "station_blog" }));
    }).catch(() => {});
  }, []);
  const loadBatches = useCallback(() => {
    api.get("/blog-bulk/batches?limit=5").then((r) => setBatches(r.data.batches)).catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); loadBatches(); }, [loadMeta, loadBatches]);

  const anyRunning = batches.some((b) => b.status === "running");
  useEffect(() => {
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(loadBatches, 5000);
    } else if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null; loadMeta();
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [anyRunning, loadBatches, loadMeta]);

  const cleanTopics = topics.map((t) => t.trim()).filter(Boolean);
  const wc = meta?.word_counts?.find((w) => w.id === s.word_count);
  const perPost = s.provider_mode === "byok" ? 0
    : (wc?.credits || 0) + (s.web_search ? (meta?.web_search_extra_credits || 5) : 0);
  const estimate = perPost * cleanTopics.length;

  const setTopic = (i, v) => setTopics((arr) => arr.map((t, j) => (j === i ? v : t)));
  const addTopic = () => topics.length < (meta?.max_topics || 25) && setTopics((a) => [...a, ""]);
  const removeTopic = (i) => setTopics((a) => (a.length > 1 ? a.filter((_, j) => j !== i) : a));

  const expand = async () => {
    setExpanding(true);
    try {
      const { data } = await api.post("/blog-bulk/topics/expand", { theme, count: themeCount });
      setTopics(data.topics);
      toast.success(`${data.topics.length} topic angles generated — review and edit before running`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Topic generation failed");
    } finally { setExpanding(false); }
  };

  const run = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/blog-bulk/batches", { topics: cleanTopics, ...s, byok_provider: s.byok_provider || null });
      toast.success(`Batch started — ${cleanTopics.length} posts queued${data.estimated_credits ? ` (~${data.estimated_credits} credits)` : ""}`);
      setTopics([""]); setTheme("");
      loadBatches();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Couldn't start batch");
    } finally { setCreating(false); }
  };

  const openReview = async (postId) => {
    try { const { data } = await api.get(`/blog-bulk/posts/${postId}`); setReview(data); }
    catch { toast.error("Couldn't load post"); }
  };

  if (!meta) return <div className="p-10 text-center text-sm text-ink-muted"><Loader2 className="h-5 w-5 mx-auto animate-spin text-brand-amber mb-2" />Loading…</div>;

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]" data-testid="bulk-batch-tab">
      <div className="space-y-6">
        {/* Theme expansion */}
        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber mb-1">// Start from a theme (optional)</div>
          <p className="text-xs text-ink-secondary mb-3">Describe a theme and AI expands it into distinct topic angles — you review and edit the list before anything is generated.</p>
          <div className="flex flex-wrap gap-2">
            <input value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="bulk-theme-input"
              placeholder='e.g. "90s hip-hop anniversaries this quarter"'
              className="flex-1 min-w-[240px] rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60" />
            <select value={themeCount} onChange={(e) => setThemeCount(Number(e.target.value))} data-testid="bulk-theme-count" className={`${selCls} !w-28`}>
              {[5, 10, 15, 20, 25].map((n) => <option key={n} value={n}>{n} topics</option>)}
            </select>
            <button type="button" onClick={expand} disabled={expanding || theme.trim().length < 5} data-testid="bulk-expand-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-40">
              {expanding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate topics
            </button>
          </div>
        </div>

        {/* Topics list */}
        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// Topics ({cleanTopics.length}/{meta.max_topics})</div>
            <button type="button" onClick={addTopic} disabled={topics.length >= meta.max_topics} data-testid="bulk-add-topic-btn"
              className="inline-flex items-center gap-1 rounded-md border border-line px-2.5 py-1 text-[11px] text-ink-secondary hover:text-ink-primary disabled:opacity-40">
              <Plus className="h-3 w-3" /> Add row
            </button>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1" data-testid="bulk-topics-list">
            {topics.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="font-mono text-[9px] text-ink-muted w-5 text-right">{i + 1}.</span>
                <input value={t} onChange={(e) => setTopic(i, e.target.value)} data-testid={`bulk-topic-input-${i}`}
                  placeholder="Blog post topic / working title…"
                  className="flex-1 rounded-md border border-line bg-bg px-3 py-1.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60" />
                <button type="button" onClick={() => removeTopic(i)} className="text-ink-muted hover:text-rose-300" aria-label="Remove topic">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Recent batches */}
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="bulk-batches-list">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">// Recent batches</div>
          {batches.length === 0 && <div className="text-xs text-ink-muted py-3">No batches yet — your first run will appear here with per-post progress.</div>}
          <div className="space-y-4">
            {batches.map((b) => {
              const done = b.posts.filter((p) => !["queued", "generating"].includes(p.status)).length;
              return (
                <div key={b.batch_id} className="rounded-lg border border-line bg-bg p-4" data-testid={`bulk-batch-${b.batch_id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-ink-primary">{new Date(b.created_at).toLocaleString()}</span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">{b.settings.word_count} · {b.settings.tone}{b.settings.web_search ? " · web search" : ""}{b.settings.provider_mode === "byok" ? ` · BYOK ${b.settings.byok_provider}` : ""}</span>
                    <span className={`ml-auto font-mono text-[9px] uppercase tracking-wider ${b.status === "running" ? "text-brand-amber" : b.status === "completed" ? "text-emerald-300" : b.status === "failed" ? "text-rose-300" : "text-amber-300"}`}>
                      {b.status === "running" ? `${done}/${b.topics_count} done` : b.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {b.posts.map((p) => {
                      const m = STATUS_META[p.status] || STATUS_META.queued;
                      return (
                        <div key={p.post_id} className="flex items-center gap-2 text-xs py-1 border-b border-line/50 last:border-0">
                          <span className={`h-2 w-2 rounded-full shrink-0 ${m.dot}`} />
                          <button type="button" onClick={() => !["queued", "generating"].includes(p.status) && openReview(p.post_id)}
                            data-testid={`bulk-post-row-${p.post_id}`}
                            className={`truncate text-left flex-1 ${["queued", "generating"].includes(p.status) ? "text-ink-muted cursor-default" : "text-ink-secondary hover:text-brand-amber"}`}>
                            {p.title || p.topic}
                          </button>
                          {p.status === "scheduled" && p.scheduled_for && (
                            <span className="font-mono text-[9px] text-violet-300">{new Date(p.scheduled_for).toLocaleDateString()}</span>
                          )}
                          {p.status === "published" && p.published_link && (
                            <a href={p.published_link.startsWith("/") ? p.published_link : p.published_link} target="_blank" rel="noreferrer"
                              className="text-emerald-300 hover:underline inline-flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                          {p.status === "failed" && <span className="text-[10px] text-rose-300 truncate max-w-[180px]" title={p.error}>{p.error}</span>}
                          <span className="font-mono text-[9px] uppercase text-ink-muted w-16 text-right">{m.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Settings sidebar */}
      <div className="space-y-4">
        <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// Batch settings</div>
          <div>
            <label className="text-[11px] text-ink-secondary block mb-1">Word count</label>
            <select value={s.word_count} onChange={(e) => setS({ ...s, word_count: e.target.value })} className={selCls} data-testid="bulk-wordcount-select">
              {meta.word_counts.map((w) => <option key={w.id} value={w.id}>{w.label} · {w.credits} cr</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-ink-secondary block mb-1">Tone</label>
            <select value={s.tone} onChange={(e) => setS({ ...s, tone: e.target.value })} className={selCls} data-testid="bulk-tone-select">
              {meta.tones.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={s.seo} onChange={(e) => setS({ ...s, seo: e.target.checked })} className="mt-0.5" data-testid="bulk-seo-toggle" />
            <span className="text-xs text-ink-secondary">SEO optimization — keyword structure, H2/H3 headings, meta description</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={s.web_search} onChange={(e) => setS({ ...s, web_search: e.target.checked })} className="mt-0.5" data-testid="bulk-websearch-toggle" />
            <span className="text-xs text-ink-secondary">Include facts from the web <span className="text-brand-amber">(+{meta.web_search_extra_credits} cr/post)</span></span>
          </label>
          {s.web_search && (
            <div className="rounded-md border border-line bg-bg px-3 py-2 text-[10px] text-ink-muted" data-testid="bulk-websearch-note">
              Live search runs per topic; the AI paraphrases the facts and cites sources at the end of each post. Raw search results are discarded after generation.
            </div>
          )}
          <div>
            <label className="text-[11px] text-ink-secondary block mb-1">Publish schedule</label>
            <select value={s.schedule} onChange={(e) => setS({ ...s, schedule: e.target.value })} className={selCls} data-testid="bulk-schedule-select">
              <option value="draft">Save all as drafts (review first)</option>
              <option value="daily">Auto-publish 1 per day</option>
              <option value="three_per_week">Auto-publish 3 per week</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-ink-secondary block mb-1">Publish to</label>
            <select value={s.publish_target} onChange={(e) => setS({ ...s, publish_target: e.target.value })} className={selCls} data-testid="bulk-target-select">
              <option value="wordpress" disabled={!meta.wordpress_connected}>
                WordPress{meta.wordpress_connected ? ` (${meta.wordpress_site})` : " — not connected"}
              </option>
              <option value="station_blog">Station blog page ({meta.station_blog_url})</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-ink-secondary block mb-1">Featured image</label>
            <select value={s.image_strategy} onChange={(e) => setS({ ...s, image_strategy: e.target.value })} className={selCls} data-testid="bulk-image-strategy-select">
              <option value="branded_card">Branded title card (auto-generated)</option>
              <option value="unsplash_photo" disabled={!meta.unsplash_available}>
                Photo (Unsplash){!meta.unsplash_available ? " — not configured" : ""}
              </option>
            </select>
            {s.image_strategy === "unsplash_photo" && (
              <div className="mt-1.5 rounded-md border border-line bg-bg px-3 py-2 text-[10px] text-ink-muted" data-testid="bulk-unsplash-note">
                A topic-matched Unsplash photo becomes the featured image, with a
                "Photo by … on Unsplash" credit added at the end of each article.
                Falls back to the branded card when no fitting photo is found.
              </div>
            )}
          </div>
          <div data-testid="bulk-share-section">
            <label className="text-[11px] text-ink-secondary block mb-1.5">Auto-share published posts to socials</label>
            {meta.share_platforms_available?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {meta.share_platforms_available.map((c) => {
                  const on = s.share_platforms.includes(c.platform);
                  return (
                    <button key={c.platform} type="button" data-testid={`bulk-share-${c.platform}`}
                      onClick={() => setS({ ...s, share_platforms: on ? s.share_platforms.filter((x) => x !== c.platform) : [...s.share_platforms, c.platform] })}
                      className={`rounded-md border px-2.5 py-1 text-[11px] font-mono capitalize transition ${
                        on ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                           : "border-line bg-bg-elevated text-ink-muted hover:text-ink-primary"}`}>
                      {c.platform}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-[10px] text-ink-muted">
                No social connectors yet — connect pages on the{" "}
                <a href="/dashboard/social-connections" className="text-brand-amber hover:underline">Social Connections page</a>.
              </div>
            )}
            {s.share_platforms.includes("instagram") && (
              <div className="mt-1.5 text-[10px] text-ink-muted" data-testid="bulk-share-ig-note">
                Each post gets a featured image (Unsplash photo or branded title card), so Instagram sharing works out of the box.
              </div>
            )}
          </div>
        </div>

        {/* Provider */}
        <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-3" data-testid="bulk-provider-panel">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// AI provider</div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" checked={s.provider_mode === "platform"} onChange={() => setS({ ...s, provider_mode: "platform" })} className="mt-0.5" data-testid="bulk-provider-platform" />
            <span className="text-xs text-ink-secondary"><span className="text-ink-primary font-semibold">Platform default</span> — uses your BroadcastFlow credits</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" checked={s.provider_mode === "byok"} onChange={() => setS({ ...s, provider_mode: "byok" })} className="mt-0.5" data-testid="bulk-provider-byok" />
            <span className="text-xs text-ink-secondary"><span className="text-ink-primary font-semibold">My own API key</span> — 0 platform credits, billed by your provider</span>
          </label>
          {s.provider_mode === "byok" && (
            meta.byok_providers.length ? (
              <select value={s.byok_provider} onChange={(e) => setS({ ...s, byok_provider: e.target.value })} className={selCls} data-testid="bulk-byok-select">
                <option value="">Choose a connected key…</option>
                {meta.byok_providers.map((p) => <option key={p.provider} value={p.provider}>{p.label} · {p.model}</option>)}
              </select>
            ) : (
              <div className="rounded-md border border-brand-amber/40 bg-brand-amber/5 px-3 py-2 text-[11px] text-ink-secondary">
                <KeyRound className="h-3 w-3 inline mr-1 text-brand-amber" />
                No keys connected yet — add an OpenAI, Anthropic or OpenRouter key on the{" "}
                <a href="/dashboard/ai-providers" className="text-brand-amber hover:underline">AI Providers page</a>.
              </div>
            )
          )}
        </div>

        {/* Estimate + run */}
        <div className="rounded-xl border border-brand-amber/40 bg-brand-amber/[0.04] p-5" data-testid="bulk-estimate-box">
          <div className="flex items-center gap-2 text-xs text-ink-secondary">
            <Coins className="h-4 w-4 text-brand-amber" />
            {s.provider_mode === "byok"
              ? <span><strong className="text-ink-primary">0 platform credits</strong> — billed directly by your provider</span>
              : <span>Estimated <strong className="text-ink-primary" data-testid="bulk-estimate-value">{estimate} credits</strong> for {cleanTopics.length} post{cleanTopics.length === 1 ? "" : "s"} ({perPost}/post){meta.available_credits != null ? ` · ${meta.available_credits} available` : ""}</span>}
          </div>
          <button type="button" onClick={run} data-testid="bulk-run-btn"
            disabled={creating || cleanTopics.length === 0 || (s.provider_mode === "byok" && !s.byok_provider)}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black amber-glow disabled:opacity-40">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Generate {cleanTopics.length || ""} post{cleanTopics.length === 1 ? "" : "s"}
          </button>
          <div className="mt-2 text-[10px] text-ink-muted text-center">Posts generate sequentially (rate-limited). You can leave this page — progress continues server-side.</div>
        </div>
      </div>

      {review && <ReviewModal post={review} onClose={() => setReview(null)} onChanged={() => { setReview(null); loadBatches(); }} />}
    </div>
  );
}

export function ReviewModal({ post, onClose, onChanged }) {
  const [title, setTitle] = useState(post.title);
  const [html, setHtml] = useState(post.content_html);
  const [imageUrl, setImageUrl] = useState(post.image_url);
  const [imageCredit, setImageCredit] = useState(post.image_credit);
  const [busy, setBusy] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const imgFileRef = useRef(null);
  const [showImg, setShowImg] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedVal, setSchedVal] = useState(() => {
    const dt = post.scheduled_for ? new Date(post.scheduled_for) : new Date(Date.now() + 24 * 3600 * 1000);
    dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
    return dt.toISOString().slice(0, 16);
  });
  const canPublish = ["draft", "scheduled", "failed"].includes(post.status) && html;

  const schedule = async () => {
    if (!schedVal) { toast.error("Pick a date and time"); return; }
    setBusy("schedule");
    try {
      if (title !== post.title || html !== post.content_html) {
        await api.patch(`/blog-bulk/posts/${post.post_id}`, { title, content_html: html });
      }
      const { data } = await api.post(`/blog-bulk/posts/${post.post_id}/schedule`, {
        scheduled_for: new Date(schedVal).toISOString(),
      });
      toast.success(`Scheduled for ${new Date(data.scheduled_for).toLocaleString()} — it'll publish itself`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Scheduling failed"); }
    finally { setBusy(""); }
  };

  const unschedule = async () => {
    setBusy("unschedule");
    try {
      await api.post(`/blog-bulk/posts/${post.post_id}/unschedule`);
      toast.success("Back to draft — it won't auto-publish");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't unschedule"); }
    finally { setBusy(""); }
  };

  const uploadImage = async (blob) => {
    setBusy("image");
    try {
      const form = new FormData();
      form.append("file", blob, "featured.jpg");
      const { data } = await api.post(`/blog-bulk/posts/${post.post_id}/image`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(`${data.image_url}?v=${Date.now()}`);
      setImageCredit(null);
      setHtml((h) => h.replace(/<p class="photo-credit"[\s\S]*?<\/p>/g, ""));
      toast.success("Featured image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy("");
      if (imgFileRef.current) imgFileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/blog-bulk/posts/${post.post_id}`, { title, content_html: html });
      toast.success("Draft saved"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };
  const publish = async () => {
    setBusy("publish");
    try {
      if (title !== post.title || html !== post.content_html) {
        await api.patch(`/blog-bulk/posts/${post.post_id}`, { title, content_html: html });
      }
      const { data } = await api.post(`/blog-bulk/posts/${post.post_id}/publish`, {});
      toast.success("Published!" + (data.link ? " Opening…" : ""));
      if (data.link) window.open(data.link.startsWith("/") ? data.link : data.link, "_blank");
      onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Publish failed");
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="bulk-review-modal">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-line bg-bg-elevated p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">
            // Review draft · {post.status}{post.credits_cost ? ` · ${post.credits_cost} cr` : ""}
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="bulk-review-close"><XIcon className="h-4 w-4" /></button>
        </div>
        {post.error && <div className="mb-3 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{post.error}</div>}
        {imageUrl ? (
          <button type="button" onClick={() => setShowImg(true)} title="Click to enlarge"
            className="group relative block w-full cursor-zoom-in rounded-lg focus:outline-none mb-1"
            data-testid="bulk-review-image-zoom">
            <img src={imageUrl} alt="" data-testid="bulk-review-image"
              className="w-full max-h-56 object-cover rounded-lg border border-line group-hover:border-brand-amber/60 transition" />
            <span className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/30 transition grid place-items-center">
              <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition" />
            </span>
          </button>
        ) : post.status !== "published" ? (
          <button type="button" onClick={() => setShowImg(true)}
            className="w-full rounded-lg border border-dashed border-line hover:border-sky-400/70 bg-bg py-6 grid place-items-center mb-1 transition group"
            data-testid="bulk-review-find-image-btn">
            <span className="inline-flex items-center gap-2 text-xs text-ink-secondary group-hover:text-sky-300 transition">
              <ImagePlus className="h-4 w-4" /> Find a featured image — search Unsplash
            </span>
          </button>
        ) : null}
        {showImg && (
          <ImageSwapLightbox
            imageUrl={imageUrl}
            title={title}
            creditText={imageCredit?.photographer ? `Photo by ${imageCredit.photographer} on Unsplash` : ""}
            credit={imageCredit?.photographer ? imageCredit : null}
            altsUrl={`/blog-bulk/posts/${post.post_id}/image/alternatives`}
            selectUrl={`/blog-bulk/posts/${post.post_id}/image/select`}
            unsplashOptionsUrl={`/blog-bulk/posts/${post.post_id}/image/unsplash-options`}
            unsplashSelectUrl={`/blog-bulk/posts/${post.post_id}/image/unsplash-select`}
            autoSearch={!imageUrl}
            onClose={() => setShowImg(false)}
            onSelected={(data) => {
              setImageUrl(data.image_url);
              setImageCredit(data.image_credit || null);
              if (data.content_html) setHtml(data.content_html);
            }}
            allowSwap={post.status !== "published"}
            altAspectClass="aspect-[1200/630]"
            testIdBase="bulk-review"
          />
        )}
        <div className="mb-3 flex items-center justify-between gap-2">
          {imageCredit?.photographer ? (
            <div className="text-[10px] text-ink-muted" data-testid="bulk-review-image-credit">
              Photo by{" "}
              <a href={imageCredit.photographer_url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">{imageCredit.photographer}</a>
              {" "}on{" "}
              <a href={imageCredit.unsplash_url || "https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral"} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">Unsplash</a>
            </div>
          ) : <span />}
          <input ref={imgFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); }} data-testid="bulk-review-upload-input" />
          {post.status !== "published" && (
            <button type="button" onClick={() => imgFileRef.current?.click()} disabled={!!busy}
              data-testid="bulk-review-upload-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary hover:border-brand-amber/50 disabled:opacity-40 shrink-0">
              {busy === "image" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              Upload my own image
            </button>
          )}
          {cropFile && (
            <ImageCropModal file={cropFile} aspect={1200 / 630} maxWidth={1600} title="Frame featured image"
              onCancel={() => { setCropFile(null); if (imgFileRef.current) imgFileRef.current.value = ""; }}
              onCropped={(blob) => { setCropFile(null); uploadImage(blob); }} />
          )}
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="bulk-review-title"
          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-sm font-semibold text-ink-primary mb-3 focus:outline-none focus:border-brand-amber/60" />
        <div className="flex items-center gap-1 mb-1.5">
          <button type="button" onClick={() => setHtmlMode(false)} data-testid="bulk-review-mode-preview"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${!htmlMode ? "border-brand-amber/60 text-brand-amber bg-brand-amber/10" : "border-line text-ink-muted hover:text-ink-primary"}`}>
            Preview
          </button>
          <button type="button" onClick={() => setHtmlMode(true)} data-testid="bulk-review-mode-html"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${htmlMode ? "border-brand-amber/60 text-brand-amber bg-brand-amber/10" : "border-line text-ink-muted hover:text-ink-primary"}`}>
            Edit HTML
          </button>
          {!htmlMode && <span className="text-[10px] text-ink-muted ml-1">this is how the published post will look</span>}
        </div>
        {htmlMode ? (
          <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={16} data-testid="bulk-review-html"
            className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs font-mono text-ink-secondary focus:outline-none focus:border-brand-amber/60" />
        ) : (
          <div data-testid="bulk-review-preview"
            className="w-full rounded-md border border-line bg-bg px-4 py-3 max-h-[46vh] overflow-y-auto text-sm text-ink-secondary leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-ink-primary [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-ink-primary [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2.5 [&_a]:text-sky-300 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: html }} />
        )}
        {(post.sources || []).length > 0 && (
          <div className="mt-3">
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">Cited sources</div>
            {post.sources.map((src, i) => (
              <a key={i} href={src.url} target="_blank" rel="noreferrer" className="block text-[11px] text-sky-300 hover:underline truncate">
                <Globe2 className="h-3 w-3 inline mr-1" />{src.title || src.url}
              </a>
            ))}
          </div>
        )}
        {(post.social_share || []).length > 0 && (
          <div className="mt-3" data-testid="bulk-review-share-results">
            <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">Social shares</div>
            {post.social_share.map((r, i) => (
              <div key={i} className="text-[11px] py-0.5">
                <span className="capitalize text-ink-secondary">{r.platform}</span>{" — "}
                <span className={r.status === "queued" ? "text-emerald-300" : "text-amber-300"}>
                  {r.status}{r.reason ? ` (${r.reason})` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2 justify-end items-center">
          {post.status === "scheduled" && post.scheduled_for && !schedOpen && (
            <span className="mr-auto inline-flex items-center gap-1.5 text-[11px] text-sky-300 font-mono" data-testid="bulk-review-scheduled-info">
              <CalendarClock className="h-3.5 w-3.5" />
              scheduled {new Date(post.scheduled_for).toLocaleString()}
              <button type="button" onClick={unschedule} disabled={!!busy} data-testid="bulk-review-unschedule"
                className="ml-1 inline-flex items-center gap-1 text-[10px] text-ink-muted hover:text-red-300 underline transition disabled:opacity-50">
                {busy === "unschedule" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarX2 className="h-3 w-3" />} unschedule
              </button>
            </span>
          )}
          {schedOpen && canPublish && (
            <span className="mr-auto inline-flex items-center gap-2 flex-wrap" data-testid="bulk-review-schedule-panel">
              <input type="datetime-local" value={schedVal} onChange={(e) => setSchedVal(e.target.value)}
                data-testid="bulk-review-schedule-input"
                className="rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none focus:border-sky-400 [color-scheme:dark]" />
              <button type="button" onClick={schedule} disabled={!!busy} data-testid="bulk-review-schedule-confirm"
                className="inline-flex items-center gap-1.5 rounded-md bg-sky-500/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-500 disabled:opacity-50">
                {busy === "schedule" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />}
                {post.status === "scheduled" ? "Update schedule" : "Confirm schedule"}
              </button>
              <button type="button" onClick={() => setSchedOpen(false)} className="text-[11px] text-ink-muted hover:text-ink-primary underline">cancel</button>
            </span>
          )}
          <button type="button" onClick={save} disabled={!!busy} data-testid="bulk-review-save"
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-4 py-2 text-xs font-semibold text-ink-primary hover:border-brand-amber/50 disabled:opacity-50">
            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save draft
          </button>
          {canPublish && !schedOpen && (
            <button type="button" onClick={() => setSchedOpen(true)} disabled={!!busy} data-testid="bulk-review-schedule-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-400/50 px-4 py-2 text-xs font-semibold text-sky-300 hover:bg-sky-400/10 disabled:opacity-50">
              <CalendarClock className="h-3.5 w-3.5" /> {post.status === "scheduled" ? "Reschedule" : "Schedule"}
            </button>
          )}
          {canPublish && (
            <button type="button" onClick={publish} disabled={!!busy} data-testid="bulk-review-publish"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
              {busy === "publish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Publish now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
